import { useState, useEffect } from "react";
import axios from "axios";
import {
  TextField,
  Button,
  Typography,
  Alert,
  Box,
  Grid,
  Paper,
  IconButton,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import SaveIcon from "@mui/icons-material/Save";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:3001";

// 毛玻璃卡片
const GlassCard = styled(Paper)(({ theme }) => ({
  background: "rgba(0, 0, 0, 0.25)",
  backdropFilter: "blur(15px)",
  WebkitBackdropFilter: "blur(15px)",
  borderRadius: "16px",
  padding: theme.spacing(4),
  border: "1px solid rgba(255, 255, 255, 0.2)",
  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2)",
  position: "relative",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(255, 255, 255, 0.05)",
    borderRadius: 16,
    pointerEvents: "none",
  },
}));

// 毛玻璃输入框
const GlassTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    backgroundColor: "rgba(255, 255, 255, 0.05)",
    borderRadius: "8px",
    "& fieldset": {
      borderColor: "rgba(255, 255, 255, 0.2)",
    },
    "&:hover fieldset": {
      borderColor: "rgba(255, 255, 255, 0.3)",
    },
    "&.Mui-focused fieldset": {
      borderColor: "rgba(255, 255, 255, 0.5)",
    },
  },
  "& .MuiInputLabel-root": {
    color: "rgba(255, 255, 255, 0.8)",
    "&.Mui-focused": {
      color: "rgba(255, 255, 255, 0.95)",
    },
  },
  "& .MuiInputBase-input": {
    color: "rgba(255, 255, 255, 0.95)",
  },
  "& .MuiFormHelperText-root": {
    color: "rgba(255, 255, 255, 0.7)",
    textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
  },
  marginBottom: theme.spacing(3),
}));

// 毛玻璃按钮
const GlassButton = styled(Button)(({ theme }) => ({
  background: "rgba(0, 0, 0, 0.6)",
  backdropFilter: "blur(10px)",
  WebkitBackdropFilter: "blur(10px)",
  borderRadius: "8px",
  color: "white",
  padding: theme.spacing(1, 3),
  "&:hover": {
    background: "rgba(0, 0, 0, 0.7)",
  },
  "&:disabled": {
    background: "rgba(0, 0, 0, 0.3)",
    color: "rgba(255, 255, 255, 0.5)",
  },
}));

const IntroManagement = () => {
  const [sections, setSections] = useState([
    {
      sectionId: 1,
      title: "1. 3C基督徒联盟是什么？",
      descriptions: [""],
    },
    {
      sectionId: 2,
      title: "2. 3C基督徒联盟的异象是什么？",
      descriptions: [""],
    },
    {
      sectionId: 3,
      title: "3. 3C基督徒联盟的策略是什么？",
      descriptions: [""],
    },
    {
      sectionId: 4,
      title: "4. 3C基督徒联盟的认信信条是什么？",
      descriptions: [""],
    },
  ]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");

  // 获取简介内容
  useEffect(() => {
    fetchIntroContent();
  }, []);

  const fetchIntroContent = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/intro-content`);
      if (response.data && response.data.length > 0) {
        // 按 sectionId 排序
        const sortedSections = response.data.sort(
          (a, b) => a.sectionId - b.sectionId
        );
        setSections(sortedSections);
      }
    } catch (err) {
      console.error("获取简介内容失败:", err);
      // 如果获取失败，使用默认内容
    } finally {
      setLoading(false);
    }
  };

  // 更新单个描述
  const handleDescriptionChange = (sectionIndex, descIndex, value) => {
    const newSections = [...sections];
    newSections[sectionIndex].descriptions[descIndex] = value;
    setSections(newSections);
  };

  // 添加描述
  const handleAddDescription = (sectionIndex) => {
    const newSections = [...sections];
    newSections[sectionIndex].descriptions.push("");
    setSections(newSections);
  };

  // 删除描述
  const handleDeleteDescription = (sectionIndex, descIndex) => {
    const newSections = [...sections];
    if (newSections[sectionIndex].descriptions.length > 1) {
      newSections[sectionIndex].descriptions.splice(descIndex, 1);
      setSections(newSections);
    }
  };

  // 添加新的section（仅在前端添加，不立即保存到后端）
  const handleAddSection = () => {
    // 查找当前最大的sectionId
    const maxSectionId =
      sections.length > 0
        ? Math.max(...sections.map((s) => s.sectionId || 0))
        : 0;

    const newSection = {
      sectionId: maxSectionId + 1,
      title: "新简介部分",
      descriptions: ["待填写"],
    };

    setSections([...sections, newSection]);
  };

  // 上移section
  const handleMoveUp = (sectionIndex) => {
    if (sectionIndex === 0) return; // 已经在最上面

    const newSections = [...sections];
    const temp = newSections[sectionIndex];
    newSections[sectionIndex] = newSections[sectionIndex - 1];
    newSections[sectionIndex - 1] = temp;
    setSections(newSections);
  };

  // 下移section
  const handleMoveDown = (sectionIndex) => {
    if (sectionIndex === sections.length - 1) return; // 已经在最下面

    const newSections = [...sections];
    const temp = newSections[sectionIndex];
    newSections[sectionIndex] = newSections[sectionIndex + 1];
    newSections[sectionIndex + 1] = temp;
    setSections(newSections);
  };

  // 删除整个section
  const handleDeleteSection = async (sectionIndex) => {
    if (sections.length <= 1) {
      setError("至少需要保留一个简介部分");
      return;
    }

    const sectionToDelete = sections[sectionIndex];

    try {
      setLoading(true);
      setError("");

      // 调用后端删除接口
      await axios.delete(
        `${API_BASE_URL}/intro-content/${sectionToDelete.sectionId}`
      );

      // 重新获取所有内容以更新列表
      await fetchIntroContent();

      setSuccess("删除成功！");
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      console.error("删除失败:", err);
      setError(err.response?.data?.message || "删除失败，请稍后重试");
    } finally {
      setLoading(false);
    }
  };

  // 更新标题
  const handleTitleChange = (sectionIndex, value) => {
    const newSections = [...sections];
    newSections[sectionIndex].title = value;
    setSections(newSections);
  };

  // 保存所有内容
  const handleSave = async () => {
    try {
      setLoading(true);
      setError("");

      // 验证数据
      for (const section of sections) {
        if (!section.title || section.title.trim() === "") {
          setError(`第 ${section.sectionId} 部分的标题不能为空`);
          setLoading(false);
          return;
        }
        if (!section.descriptions || section.descriptions.length === 0) {
          setError(`第 ${section.sectionId} 部分至少需要一条描述`);
          setLoading(false);
          return;
        }
        // 过滤掉空描述
        const validDescriptions = section.descriptions.filter(
          (desc) => desc && desc.trim() !== ""
        );
        if (validDescriptions.length === 0) {
          setError(`第 ${section.sectionId} 部分至少需要一条有效的描述`);
          setLoading(false);
          return;
        }
      }

      // 准备保存的数据，过滤掉空描述，并更新sectionId以反映新的顺序
      const sectionsToSave = sections.map((section, index) => ({
        sectionId: index + 1, // 根据当前顺序重新分配sectionId
        originalSectionId: section.sectionId, // 保留原始sectionId用于查找
        title: section.title.trim(),
        descriptions: section.descriptions
          .filter((desc) => desc && desc.trim() !== "")
          .map((desc) => desc.trim()),
        _id: section._id, // 保留_id用于判断是否已存在
      }));

      // 分离已存在的section和新创建的section
      const existingSections = sectionsToSave.filter((s) => s._id);
      const newSections = sectionsToSave.filter((s) => !s._id);

      // 批量更新已存在的section（包括更新sectionId以反映新顺序）
      if (existingSections.length > 0) {
        await axios.put(`${API_BASE_URL}/intro-content`, {
          sections: existingSections,
        });
      }

      // 创建新的section
      for (const section of newSections) {
        await axios.post(`${API_BASE_URL}/intro-content`, {
          title: section.title,
          descriptions: section.descriptions,
        });
      }

      // 重新获取所有内容以更新列表（包括新创建的_id）
      await fetchIntroContent();

      setSuccess("保存成功！");
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      console.error("保存失败:", err);
      setError(err.response?.data?.message || "保存失败，请稍后重试");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3, width: "100%" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 4,
        }}
      >
        <Typography
          variant="h4"
          sx={{
            fontWeight: 500,
            color: "#fff",
            textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
            position: "relative",
            zIndex: 1,
          }}
        >
          简介内容管理
        </Typography>
        <GlassButton
          variant="contained"
          onClick={handleAddSection}
          disabled={loading}
          startIcon={<AddIcon />}
          sx={{ px: 3, py: 1.2 }}
        >
          添加简介部分
        </GlassButton>
      </Box>

      {success && (
        <Alert
          severity="success"
          sx={{
            mb: 3,
            backgroundColor: "rgba(237, 247, 237, 0.6)",
            border: "1px solid rgba(46, 125, 50, 0.1)",
            borderRadius: "8px",
          }}
          onClose={() => setSuccess("")}
        >
          {success}
        </Alert>
      )}

      {error && (
        <Alert
          severity="error"
          sx={{
            mb: 3,
            "& .MuiAlert-message": { color: "#fff" },
            "& .MuiAlert-icon": { color: "#fff" },
            backgroundColor: "rgba(244, 67, 54, 0.2)",
          }}
          onClose={() => setError("")}
        >
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {sections.map((section, sectionIndex) => (
          <Grid item xs={12} key={section.sectionId}>
            <GlassCard>
              <Box sx={{ position: "relative", zIndex: 1 }}>
                {/* 标题区域，带顺序调整和删除按钮 */}
                <Box
                  sx={{
                    display: "flex",
                    gap: 1,
                    mb: 3,
                    alignItems: "flex-start",
                  }}
                >
                  <GlassTextField
                    fullWidth
                    label="标题"
                    value={section.title}
                    onChange={(e) =>
                      handleTitleChange(sectionIndex, e.target.value)
                    }
                    sx={{ mb: 0 }}
                  />
                  {/* 顺序调整按钮 */}
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}
                  >
                    <IconButton
                      onClick={() => handleMoveUp(sectionIndex)}
                      disabled={sectionIndex === 0}
                      sx={{
                        color: "rgba(255, 255, 255, 0.8)",
                        padding: "4px",
                        "&:hover": {
                          backgroundColor: "rgba(255, 255, 255, 0.1)",
                        },
                        "&:disabled": {
                          color: "rgba(255, 255, 255, 0.3)",
                        },
                      }}
                      title="上移"
                    >
                      <ArrowUpwardIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      onClick={() => handleMoveDown(sectionIndex)}
                      disabled={sectionIndex === sections.length - 1}
                      sx={{
                        color: "rgba(255, 255, 255, 0.8)",
                        padding: "4px",
                        "&:hover": {
                          backgroundColor: "rgba(255, 255, 255, 0.1)",
                        },
                        "&:disabled": {
                          color: "rgba(255, 255, 255, 0.3)",
                        },
                      }}
                      title="下移"
                    >
                      <ArrowDownwardIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  {/* 删除按钮 */}
                  {sections.length > 1 && (
                    <IconButton
                      onClick={() => handleDeleteSection(sectionIndex)}
                      sx={{
                        color: "rgba(255, 255, 255, 0.8)",
                        mt: 0.5,
                        "&:hover": {
                          backgroundColor: "rgba(255, 100, 100, 0.2)",
                          color: "rgba(255, 150, 150, 0.9)",
                        },
                      }}
                      title="删除此简介部分"
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                </Box>

                <Typography
                  variant="subtitle1"
                  sx={{
                    mb: 2,
                    fontWeight: 500,
                    color: "rgba(255, 255, 255, 0.85)",
                    textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
                  }}
                >
                  描述内容：
                </Typography>

                {section.descriptions.map((description, descIndex) => (
                  <Box
                    key={descIndex}
                    sx={{
                      display: "flex",
                      gap: 1,
                      mb: 2,
                      alignItems: "flex-start",
                    }}
                  >
                    <GlassTextField
                      fullWidth
                      label={`描述 ${descIndex + 1}`}
                      value={description}
                      onChange={(e) =>
                        handleDescriptionChange(
                          sectionIndex,
                          descIndex,
                          e.target.value
                        )
                      }
                      multiline
                      minRows={1}
                      maxRows={10}
                      sx={{ mb: 0 }}
                    />
                    {section.descriptions.length > 1 && (
                      <IconButton
                        onClick={() =>
                          handleDeleteDescription(sectionIndex, descIndex)
                        }
                        sx={{
                          color: "rgba(255, 255, 255, 0.8)",
                          mt: 1,
                          "&:hover": {
                            backgroundColor: "rgba(255, 100, 100, 0.2)",
                            color: "rgba(255, 150, 150, 0.9)",
                          },
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </Box>
                ))}

                <Button
                  startIcon={<AddIcon />}
                  onClick={() => handleAddDescription(sectionIndex)}
                  sx={{
                    color: "rgba(255, 255, 255, 0.8)",
                    mb: 3,
                  }}
                >
                  添加描述
                </Button>
              </Box>
            </GlassCard>
          </Grid>
        ))}
      </Grid>

      <Box sx={{ mt: 4, display: "flex", justifyContent: "center" }}>
        <GlassButton
          variant="contained"
          onClick={handleSave}
          disabled={loading}
          startIcon={<SaveIcon />}
          sx={{ px: 4, py: 1.5 }}
        >
          {loading ? "保存中..." : "保存所有内容"}
        </GlassButton>
      </Box>
    </Box>
  );
};

export default IntroManagement;
